import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfigDataService } from '../_services/config-data.service';
import { StateCity } from '../_models/stateCity';
import { Subscription, debounceTime } from 'rxjs';

@Component({
  selector: 'app-rsa-provider-list',
  templateUrl: './rsa-provider-list.component.html',
  styleUrls: ['./rsa-provider-list.component.less']
})
export class RsaProviderListComponent implements OnInit, OnDestroy {
  providerForm: FormGroup;
  lat: number = 28.6139; // Default to Delhi
  lng: number = 77.2090;
  markerSet = false;
  submitting = false;
  message = '';
  stateCityList: StateCity[] = [];
  stateList: string[] = [];
  cityList: string[] = [];
  filteredCities: string[] = [];
  isLoaded = false;
  
  // Subscriptions to avoid memory leaks
  private subscriptions: Subscription[] = [];

  constructor(private fb: FormBuilder, private configService: ConfigDataService) {
    this.initForm();
  }
  
  ngOnInit(): void {
    this.loadStateCity();
    
    // Set up city input value changes with debounce time
    const citySubscription = this.providerForm.get('city')?.valueChanges
      .pipe(debounceTime(300))
      .subscribe(cityInput => {
        if (cityInput && typeof cityInput === 'string') {
          this.onCityInput(cityInput);
          
          // Auto-update map when both state and city are filled
          if (this.providerForm.value.state && cityInput && this.providerForm.value.providerAddress) {
            this.updateMapToAddress(
              `${this.providerForm.value.providerAddress}, ${cityInput}, ${this.providerForm.value.state}`
            );
          }
        }
      });
      
    if (citySubscription) {
      this.subscriptions.push(citySubscription);
    }
    
    // Add animation class after a short delay
    setTimeout(() => {
      this.isLoaded = true;
    }, 100);
  }
  
  ngOnDestroy(): void {
    // Clean up subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
  
  private initForm(): void {
    this.providerForm = this.fb.group({
      providerName: ['', Validators.required],
      providerAddress: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      contactNumber: ['', [Validators.required, Validators.pattern('^[0-9]{10,}$')]],
      latitude: ['', Validators.required],
      longitude: ['', Validators.required]
    });
  }

  /**
   * Load state and city data from the service
   */
  loadStateCity() {
    this.configService.getStateCity().subscribe({
      next: (data: StateCity[]) => {
        this.stateCityList = data;
        this.stateList = Array.from(new Set(data.map(x => x.state))).sort();
      },
      error: (error) => {
        console.error('Error loading states and cities:', error);
        this.message = 'Error loading location data. Please refresh the page.';
      }
    });
  }

  /**
   * Handle state selection change
   * Updates city list based on selected state
   */
  onStateChange(state: string) {
    // Clear any previous messages
    this.message = '';
    
    const found = this.stateCityList.find(x => x.state === state);
    this.cityList = found ? found.city.sort() : [];
    this.filteredCities = this.cityList;
    
    // Reset city when state changes
    this.providerForm.patchValue({ city: '' });
    
    // If address is already filled, update map with new state
    const address = this.providerForm.value.providerAddress;
    if (address && state) {
      this.updateMapToAddress(`${address}, ${state}`);
    }
  }
  
  /**
   * Filter cities based on input text
   */
  onCityInput(cityInput: string) {
    if (!cityInput) {
      this.filteredCities = this.cityList;
      return;
    }
    
    this.filteredCities = this.cityList.filter(city => 
      city.toLowerCase().includes(cityInput.toLowerCase())
    );
  }

  /**
   * Handle city selection
   * Updates the form and map location
   */
  onCitySelect(city: string) {
    if (!city) return;
    
    this.providerForm.patchValue({ city });
    this.filteredCities = [];
    
    const state = this.providerForm.value.state;
    const address = this.providerForm.value.providerAddress;
    
    if (state && city && address) {
      this.updateMapToAddress(`${address}, ${city}, ${state}`);
    }
  }

  /**
   * Update map coordinates based on address
   * Uses Google Maps Geocoding API
   */
  updateMapToAddress(address: string) {
    if (!address) return;
    
    // Use Google Maps Geocoding API to get lat/lng for the address
    const geocoder = new (window as any).google.maps.Geocoder();
    
    geocoder.geocode({ address }, (results: any, status: any) => {
      if (status === 'OK' && results[0]) {
        this.lat = results[0].geometry.location.lat();
        this.lng = results[0].geometry.location.lng();
        
        // Update form values with the coordinates
        this.providerForm.patchValue({ 
          latitude: this.lat, 
          longitude: this.lng 
        });
        
        this.markerSet = true;
      }
    });
  }

  /**
   * Handle map click event
   * Updates coordinates in the form
   */
  onMapClick(event: any) {
    if (event.latLng) {
      this.lat = event.latLng.lat();
      this.lng = event.latLng.lng();
      
      this.providerForm.patchValue({ 
        latitude: this.lat, 
        longitude: this.lng 
      });
      
      this.markerSet = true;
    }
  }

  /**
   * Submit the form
   * Adds a new RSA provider to the system
   */
  submit() {
    if (this.providerForm.invalid) {
      // Mark all fields as touched to trigger validation messages
      Object.keys(this.providerForm.controls).forEach(key => {
        this.providerForm.get(key).markAsTouched();
      });
      return;
    }
    
    this.submitting = true;
    this.message = '';
    
    this.configService.addRsaProvider(this.providerForm.getRawValue()).subscribe({
      next: () => {
        this.message = 'Provider added successfully! The new provider is now available for RSA services.';
        this.resetForm();
      },
      error: (error) => {
        console.error('Error adding provider:', error);
        this.message = 'Error adding provider. Please check your data and try again.';
        this.submitting = false;
      }
    });
  }
  
  /**
   * Reset the form to its initial state
   */
  resetForm() {
    this.providerForm.reset();
    this.markerSet = false;
    this.submitting = false;
    this.filteredCities = [];
    
    // Reset map to default location
    this.lat = 28.6139;
    this.lng = 77.2090;
  }
}
